/* eslint-disable no-console */

import defer from 'lodash/defer';
import { getJSON, delay } from 'utils';
import { save, load } from 'helpers/storage';
import { NEWS } from 'constants/popups';
import { VERSION } from 'config';
import { news as newsModel } from 'models/news';
import { News, NewsCondition } from 'types';
import { Store } from 'stores/type';
import { DAY } from 'constants/time';
import EventsManager from 'managers/events.manager';

const URL = process.env.VUE_APP_NEWS_URL!;
const STORAGE_KEY = 'news';
const MIN_SESSIONS = 1;
const MIN_TIME_SINCE_LAST = 3 * DAY;
const MAX_FETCH_DURATION = 300;

class NewsManager {
  private $store!: Store;
  private _promise: Promise<News[]> | null = null;

  private shown: Array<News['id']>;
  private cached: News[];
  private lastShownTimestamp: number;

  constructor() {
    this.shown = this.storage.shown || [];
    this.cached = this.storage.cached || [];
    this.lastShownTimestamp = this.storage.timestamp || -1;
  }

  public init(store: Store) {
    this.$store = store;

    EventsManager.once('LIBRARY_RENDERED', this.initContent);
  }
  private initContent = async () => {
    this.fetch().then(this.fillCache);

    if (this.$store.getters.sessionsCount <= MIN_SESSIONS) return;

    const data = await this.load();
    const newsToShow = this.getNewsToShow(this.prepare(data));
    if (!newsToShow) return;

    this.show(newsToShow);
  }

  private load() {
    const fromCache = async (): Promise<News[]> => {
      await delay(MAX_FETCH_DURATION);
      return this.cached;
    };

    return Promise.race([
      this.fetch(),
      fromCache(),
    ]);
  }
  private prepare(data: News[]) {
    if (!data) return [];

    return data
      .map((news) => ({ ...newsModel, ...news }))
      .filter(({ id, offline, conditions }) => {
        const wasShown = this.shown.includes(id);
        if (wasShown) return false;

        const canShowWithConnectivity = (this.isOffline && offline) || !this.isOffline;
        const passesConditions = this.checkConditions(conditions);
        return passesConditions && canShowWithConnectivity;
      });
  }
  private getNewsToShow(data: News[]): News | null {
    if (!data || !data.length) return null;

    const immediateNews = data.find(({ immediate }) => immediate);
    if (immediateNews) {
      return immediateNews;
    }

    const timeFromLastShown = Date.now() - this.lastShownTimestamp;
    if (timeFromLastShown < MIN_TIME_SINCE_LAST) return null;

    // show first news as they are sorted by priority
    const news = data[0];
    if (news.clear) return null;

    return news;
  }

  public show(data: News) {
    defer(() => this.$store.dispatch('popupShowWithData', { name: NEWS, data }));

    EventsManager.once('PLAY', () => {
      this.shown.push(data.id);
      this.lastShownTimestamp = Date.now();
      this.save();
    });
  }

  private checkConditions(conditions: NewsCondition[]) {
    const TYPES = {
      versionEq: (value: string) => value === VERSION,
      versionGt: (value: string) => {
        const versionSplit = VERSION.split('.');
        const valueSplit = value.split('.');

        return valueSplit.some((current, index) => +current < +versionSplit[index]);
      },
      versionLt: (value: string) => {
        const versionSplit = VERSION.split('.');
        const valueSplit = value.split('.');

        return valueSplit.some((current, index) => +current > +versionSplit[index]);
      },
      expired: (value: number) => Date.now() <= value,
      after: (value: string) => this.shown.includes(value),
    };

    return conditions.every(({ type, value }) => {
      if (!TYPES.hasOwnProperty(type)) return true;

      // @ts-ignore
      return TYPES[type](value);
    });
  }

  private fillCache = (downloaded: News[] = []) => {
    let clear = false;
    const cachedIds = this.cached.map(({ id }) => id);
    downloaded.forEach((news) => {
      if (cachedIds.includes(news.id) || this.shown.includes(news.id)) {
        return;
      }

      if (typeof news.clear === 'boolean' && news.clear) {
        clear = true;
      }

      this.cached.push(news);
    });

    if (!clear) {
      // filter out already shown news from cache
      this.cached = this.cached.filter(({ id }) => !this.shown.includes(id));
    } else {
      // clear cache
      this.cached = [];
    }

    this.save();
  }
  private save() {
    save(STORAGE_KEY as any, {
      shown: this.shown,
      cached: this.cached,
      timestamp: this.lastShownTimestamp,
    });
  }

  private fetch() {
    if (this._promise !== null) {
      return this._promise;
    }

    this._promise = getJSON<News[]>(URL);
    return this._promise;
  }

  get storage() {
    return load(STORAGE_KEY as any) || {};
  }
  get isOffline() {
    return !this.$store.getters.online;
  }
}

export default new NewsManager();
